import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { t, font, Button } from "@realtyflows/ui";
import { getDocTemplate, updateDocTemplate, getTemplateFields } from "../api";
import CUSTOM_FIELD_CATALOG from "../data/customFieldCatalog";
import useResponsive from "../hooks/useResponsive";

export default function TemplateSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const isCompact = responsive.isCompact;

  const [template, setTemplate] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [customName, setCustomName] = useState("");
  const [customType, setCustomType] = useState("text");

  useEffect(() => {
    Promise.all([
      getDocTemplate(id),
      getTemplateFields(id),
    ]).then(([tmpl, fields]) => {
      setTemplate(tmpl);
      setCustomFields(tmpl.custom_fields || []);
      setPreview(fields);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateDocTemplate(id, { custom_fields: customFields });
      setTemplate(updated);
      setDirty(false);
      // Refresh preview
      const fields = await getTemplateFields(id);
      setPreview(fields);
      setToast({ message: "Fields saved", variant: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save", variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const addField = (field) => {
    setCustomFields(prev => [...prev, { key: field.key, label: field.label, type: field.type, filled_by: "agent" }]);
    setDirty(true);
  };

  const removeField = (idx) => {
    setCustomFields(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const toggleFilledBy = (idx) => {
    setCustomFields(prev => prev.map((f, i) => i === idx ? { ...f, filled_by: f.filled_by === "signer" ? "agent" : "signer" } : f));
    setDirty(true);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setCustomFields(prev => {
      const copy = [...prev];
      const [moved] = copy.splice(dragIdx, 1);
      copy.splice(idx, 0, moved);
      return copy;
    });
    setDragIdx(idx);
    setDirty(true);
  };
  const handleDragEnd = () => setDragIdx(null);

  const usedKeys = new Set([
    ...Object.keys(template?.field_map || {}),
    ...customFields.map(f => f.key),
  ]);
  const available = CUSTOM_FIELD_CATALOG.filter(f => !usedKeys.has(f.key));

  if (loading) {
    return <div style={{ padding: 40, fontSize: 13, color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  }

  if (!template) {
    return <div style={{ padding: 40, fontSize: 13, color: t.coral, fontFamily: font.body }}>Template not found</div>;
  }

  const previewFields = preview?.fields || [];
  const agentFields = previewFields.filter(f => !f.auto && f.filled_by !== "signer");
  const autoFields = previewFields.filter(f => f.auto || f.filled);
  const signerFields = previewFields.filter(f => f.filled_by === "signer");

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 900, margin: "0 auto" }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 999,
          background: toast.variant === "error" ? t.coral : t.emerald,
          color: "#fff", padding: isMobile ? "10px 14px" : "12px 20px", borderRadius: 8,
          fontSize: isMobile ? 12 : 13, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          maxWidth: isMobile ? "calc(100vw - 40px)" : "auto",
        }}>
          {toast.message}
        </div>
      )}

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobile ? 16 : 24 }}>
        <button onClick={() => navigate(-1)}
          style={{
            display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
            cursor: "pointer", color: t.textSecondary, fontSize: isMobile ? 12 : 13, fontFamily: font.body, padding: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = t.emerald)}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textSecondary)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        {dirty && (
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr",
        gap: isMobile ? 12 : 20,
      }}>
        {/* Left — Field Manager */}
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12 }}>
          <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", borderBottom: `1px solid ${t.border}`, borderRadius: "12px 12px 0 0" }}>
            <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display, wordBreak: "break-word" }}>{template.name}</div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>
              Manage custom CRM fields for this template
            </div>
          </div>

          {/* Standard fields (read-only) */}
          <div style={{ padding: isMobile ? "12px 14px" : "16px 24px", borderBottom: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
              STANDARD FIELDS
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {Object.keys(template.field_map || {}).map(key => (
                <span key={key} style={{
                  fontSize: 10, padding: "3px 8px", borderRadius: 4,
                  background: t.surfaceWarm, color: t.textSecondary, fontFamily: font.body,
                  border: `1px solid ${t.border}`,
                }}>
                  {key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              ))}
            </div>
          </div>

          {/* Custom fields (editable) */}
          <div style={{ padding: isMobile ? "12px 14px" : "16px 24px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.sky, marginBottom: 10, fontFamily: font.mono, letterSpacing: 0.5 }}>
              CUSTOM CRM FIELDS
            </div>

            {customFields.length === 0 && (
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, padding: "8px 0" }}>
                No custom fields added yet
              </div>
            )}

            {customFields.map((f, idx) => (
              <div
                key={f.key}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
                style={{
                  display: "flex", alignItems: "center", gap: isMobile ? 6 : 8,
                  padding: isMobile ? "6px 8px" : "8px 10px",
                  background: dragIdx === idx ? `${t.sky}08` : t.surfaceWarm,
                  border: `1px solid ${dragIdx === idx ? t.sky : t.border}`,
                  borderRadius: 6, marginBottom: 6, cursor: "grab",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                }}
              >
                {/* Drag handle */}
                <svg width="10" height="10" viewBox="0 0 24 24" fill={t.textMuted} style={{ flexShrink: 0, opacity: 0.4 }}>
                  <circle cx="8" cy="6" r="2"/><circle cx="16" cy="6" r="2"/>
                  <circle cx="8" cy="12" r="2"/><circle cx="16" cy="12" r="2"/>
                  <circle cx="8" cy="18" r="2"/><circle cx="16" cy="18" r="2"/>
                </svg>

                <span style={{ fontSize: 11, fontWeight: 500, color: t.textPrimary, fontFamily: font.body, flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                  {f.label}
                </span>

                <span style={{ fontSize: 8, color: t.textMuted, background: t.surface, padding: "1px 4px", borderRadius: 3, flexShrink: 0 }}>
                  {f.type}
                </span>

                {/* Filled-by toggle */}
                <div
                  onClick={() => toggleFilledBy(idx)}
                  style={{
                    fontSize: 9, fontWeight: 700, cursor: "pointer", padding: "2px 8px", borderRadius: 4,
                    color: f.filled_by === "signer" ? "#F59E0B" : t.emerald,
                    background: f.filled_by === "signer" ? "rgba(245,158,11,0.1)" : `${t.emerald}10`,
                    border: `1px solid ${f.filled_by === "signer" ? "rgba(245,158,11,0.2)" : `${t.emerald}20`}`,
                    flexShrink: 0,
                  }}
                >
                  {f.filled_by === "signer" ? "Signer" : "Agent"}
                </div>

                {/* Remove */}
                <div
                  onClick={() => removeField(idx)}
                  style={{ fontSize: 11, color: t.coral, cursor: "pointer", opacity: 0.4, padding: "0 2px", flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.opacity = 1}
                  onMouseLeave={e => e.currentTarget.style.opacity = 0.4}
                >
                  {"\u2715"}
                </div>
              </div>
            ))}

            {/* Add field */}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.sky, marginBottom: 6, fontFamily: font.mono, letterSpacing: 0.5 }}>
                ADD FIELD
              </div>

              {/* From catalog */}
              {available.length > 0 && (
                <select
                  value=""
                  onChange={e => {
                    const field = available.find(f => f.key === e.target.value);
                    if (field) addField(field);
                  }}
                  style={{
                    width: "100%", padding: "8px 10px", fontSize: 12, fontFamily: font.body,
                    background: t.surfaceWarm, color: t.textPrimary,
                    border: `1.5px solid ${t.border}`, borderRadius: 6, cursor: "pointer",
                    outline: "none", appearance: "auto", marginBottom: 8,
                  }}
                >
                  <option value="">Select from CRM fields...</option>
                  {available.map(f => (
                    <option key={f.key} value={f.key}>{f.label} ({f.type})</option>
                  ))}
                </select>
              )}

              {/* Custom field */}
              <div style={{
                display: "flex", gap: 6,
                alignItems: isMobile ? "stretch" : "flex-end",
                flexDirection: isMobile ? "column" : "row",
              }}>
                <input
                  type="text"
                  value={customName}
                  onChange={e => setCustomName(e.target.value)}
                  placeholder="Custom field name"
                  style={{
                    flex: 1, padding: "8px 10px", fontSize: 12, fontFamily: font.body,
                    background: t.surfaceWarm, color: t.textPrimary,
                    border: `1.5px solid ${t.border}`, borderRadius: 6, outline: "none",
                  }}
                  onFocus={e => e.target.style.borderColor = t.sky}
                  onBlur={e => e.target.style.borderColor = t.border}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <select
                    value={customType}
                    onChange={e => setCustomType(e.target.value)}
                    style={{
                      width: 80, padding: "8px 6px", fontSize: 11, fontFamily: font.body,
                      background: t.surfaceWarm, color: t.textPrimary,
                      border: `1.5px solid ${t.border}`, borderRadius: 6, cursor: "pointer",
                      outline: "none", appearance: "auto",
                      flex: isMobile ? 1 : "none",
                    }}
                  >
                    <option value="text">text</option>
                    <option value="date">date</option>
                    <option value="money">money</option>
                  </select>
                  <div
                    onClick={() => {
                      const name = customName.trim();
                      if (!name) return;
                      const key = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
                      if (usedKeys.has(key)) {
                        setToast({ message: "Field already exists", variant: "error" });
                        return;
                      }
                      addField({ key, label: name, type: customType });
                      setCustomName("");
                      setCustomType("text");
                    }}
                    style={{
                      padding: "8px 12px", fontSize: 11, fontWeight: 700, fontFamily: font.body,
                      background: t.sky, color: "#fff", borderRadius: 6, cursor: "pointer",
                      whiteSpace: "nowrap", flexShrink: 0,
                      textAlign: "center",
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 0.85}
                    onMouseLeave={e => e.currentTarget.style.opacity = 1}
                  >
                    Add
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right — Preview */}
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", borderBottom: `1px solid ${t.border}` }}>
            <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Field Preview</div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>
              How fields appear in the contract modal
            </div>
          </div>

          <div style={{ padding: isMobile ? "12px 14px" : "16px 24px" }}>
            {/* Agent required */}
            {agentFields.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.coral, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                  REQUIRED FIELDS
                </div>
                {agentFields.map(f => (
                  <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", border: `1.5px solid ${t.coral}`, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: t.textPrimary, fontFamily: font.body }}>{f.label}</span>
                    {f.custom && <span style={{ fontSize: 7, color: t.sky, fontWeight: 700 }}>CRM</span>}
                    <span style={{ fontSize: 8, color: t.textMuted, marginLeft: "auto" }}>{f.type || "text"}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Auto-filled */}
            {autoFields.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.emerald, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                  AUTO-FILLED
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "5px 12px" }}>
                  {autoFields.map(f => (
                    <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 9, color: t.emerald }}>&#10003;</span>
                      <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Signer */}
            {signerFields.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.amber, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                  FILLED BY SIGNER
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "5px 12px" }}>
                  {signerFields.map(f => (
                    <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round"><path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                      <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {previewFields.length === 0 && (
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, padding: "16px 0", textAlign: "center" }}>
                No fields configured
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
